import {useEffect, useState} from 'react'

import {
  FILM_STRIP_FRAMES,
  filmFrameTimesMs,
  getFilmFrameReader,
} from '#/lib/flashbookFilmFrames'

/**
 * The frames of a film, for a strip.
 *
 * Returns one slot per frame from the first render, `undefined` until that frame
 * has been read, so the strip's *geometry* is right before any picture arrives:
 * cells of equal width, the selection window sitting over them in the right
 * place, and the drag arithmetic untouched by whether the decoder has caught up.
 * A strip that only laid itself out once the pixels landed would jump.
 *
 * Reading starts when the film's length is known, because the times to sample are
 * fractions of it — asking before then would sample a film of unknown length and
 * have to be redone anyway.
 */
export function useFilmFrames(
  uri: string | undefined,
  durationMs: number | undefined,
  count: number = FILM_STRIP_FRAMES,
): (string | undefined)[] {
  const [frames, setFrames] = useState<(string | undefined)[]>(() =>
    Array.from({length: count}, () => undefined),
  )

  useEffect(() => {
    const times = filmFrameTimesMs(durationMs ?? 0, count)
    if (!uri || times.length === 0) {
      setFrames(Array.from({length: count}, () => undefined))
      return
    }

    /*
     * A generation guard, because a read is a seek at a time and outlives what
     * asked for it: closing the trimmer mid-read, or opening it on a different
     * film, must not paint the previous film's frames into the new strip.
     */
    let live = true
    const reader = getFilmFrameReader()
    void reader.read(uri, times).then(
      read => {
        if (live) setFrames(read)
      },
      () => {
        if (live) setFrames(Array.from({length: count}, () => undefined))
      },
    )

    return () => {
      live = false
    }
  }, [uri, durationMs, count])

  /*
   * Drop the film's decoded frames when the trimmer closes on it.
   *
   * A frame is held as a data URL — a real few kilobytes each — and the reader
   * also keeps a video element alive per film. Neither belongs to the studio once
   * the minute has been chosen, and a creator who trims several films in one
   * sitting would otherwise accumulate every one of them.
   */
  useEffect(() => {
    if (!uri) return
    const reader = getFilmFrameReader()
    return () => reader.release(uri)
  }, [uri])

  return frames
}
